import { NookApiError, useNook } from "@nook/api-client"
import { Button, SidebarTrigger, Skeleton } from "@nook/ui"
import { createFileRoute, Link, Navigate, useNavigate } from "@tanstack/react-router"
import { ChevronLeft, FileX, Trash2 } from "lucide-react"
import { useRef, useState } from "react"

import { EmptyState, GhostCards } from "@/components/empty-state.js"
import { useKeys } from "@/components/keys.js"
import { isNote, NoteDocument } from "@/components/note-editor.js"
import { ConfirmDelete } from "@/components/nook-panel.js"

/**
 * `/n/$id` — a note, full page, as Apple Notes opens one (block 47). `new` is a
 * blank note that exists from its first keystroke; the URL is then replaced
 * with its id and the router keeps this mounted, so the caret stays put. Any
 * other nook still lands on `/?nook=`, where block 03 moved the detail.
 */
export const Route = createFileRoute("/_authed/n/$id")({ component: NoteRoute })

function NoteRoute() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  // Which `new` this is, and the id it became: a second `Shift+N` is a second blank note.
  const draft = useRef({ key: 0, id: null as string | null, at: id })
  if (draft.current.at !== id) draft.current = id === "new" ? { key: draft.current.key + 1, id: null, at: id } : { ...draft.current, at: id }
  const fresh = id === "new" || id === draft.current.id

  return (
    <div className="flex min-h-full flex-col">
      <header className="glass sticky top-0 z-10 flex h-14 items-center gap-2 border-b px-4 md:px-6">
        <SidebarTrigger className="-ml-2 md:hidden" />
        <Button variant="ghost" size="sm" className="h-7.5 gap-1 pl-1.5 text-[0.8125rem] font-normal" asChild>
          <Link to="/notes">
            <ChevronLeft />
            Notes
          </Link>
        </Button>
        <span className="flex-1" />
        {fresh && !draft.current.id ? null : <DeleteNote id={fresh ? draft.current.id! : id} />}
      </header>

      <main className="mx-auto w-full max-w-[720px] flex-1 px-6 py-8 md:py-12">
        {fresh ? (
          <NoteDocument
            key={draft.current.key}
            nook={null}
            onCreated={(created) => {
              draft.current.id = created
              void navigate({ to: "/n/$id", params: { id: created }, replace: true })
            }}
          />
        ) : (
          <SavedNote id={id} />
        )}
      </main>
    </div>
  )
}

function SavedNote({ id }: { id: string }) {
  const nook = useNook(id)

  if (nook.isPending)
    return (
      <div className="space-y-3">
        <Skeleton className="mx-auto h-3 w-40" />
        <Skeleton className="h-9 w-2/3" />
        <Skeleton className="h-24 w-full" />
      </div>
    )
  if (nook.error instanceof NookApiError && nook.error.status === 404)
    return (
      <EmptyState
        art={<GhostCards icons={[FileX]} />}
        title="This note isn't in your library"
        description="It may have been deleted, or the link is wrong."
        action={
          <Button asChild>
            <Link to="/notes">Back to Notes</Link>
          </Button>
        }
      />
    )
  if (nook.isError) return <p className="text-destructive text-sm">Could not load this note. Try reloading.</p>
  if (!isNote(nook.data)) return <Navigate to="/" search={{ nook: id }} replace />

  return <NoteDocument key={id} nook={nook.data} />
}

function DeleteNote({ id }: { id: string }) {
  const [confirming, setConfirming] = useState<string | null>(null)
  const navigate = useNavigate()

  useKeys({ "$mod+Backspace": () => setConfirming(id) })

  return (
    <>
      <Button variant="ghost" size="icon-sm" className="size-7.5" aria-label="Delete note" onClick={() => setConfirming(id)}>
        <Trash2 />
      </Button>
      <ConfirmDelete
        id={confirming}
        onDone={(deleted) => {
          setConfirming(null)
          if (deleted) void navigate({ to: "/notes" })
        }}
      />
    </>
  )
}
